import * as Highcharts from 'highcharts';
import { find, get } from 'lodash';

import {
  formatParameters,
  IFormatOutputParameters,
} from '../../../../../Visualization/formatParameters';
import { truncationValues } from '../../../../../Visualization/visualizationConstants';

import { FormatConfig } from './types';

/**
 * Get format configuration based on format key
 */
export const getFormatConfig = (
  formatKey: number | null | undefined
): FormatConfig => {
  const formatParams = find(formatParameters, {
    formatKey,
  }) as IFormatOutputParameters;

  if (!formatParams) {
    return {
      decimalPlaces: 0,
      formatType: '',
      prefix: '',
      suffix: '',
      magnitudeTruncation: 0,
    };
  }

  const numberType = get(formatParams, 'numberType', '');

  return {
    decimalPlaces: get(formatParams, 'decimalPlaces', 0),
    formatType: numberType,
    prefix: numberType === '$' ? '$' : '',
    suffix: numberType === 'Percent' ? '%' : '',
    magnitudeTruncation: get(formatParams, 'magnitudeTruncation', 0),
  };
};

/**
 * Format a numeric value based on format configuration
 */
export const formatValue = (
  value: number,
  formatConfig: FormatConfig
): string => {
  const { formatType, prefix, suffix, decimalPlaces, magnitudeTruncation } =
    formatConfig;
  const adjustedValue =
    (formatType === 'Percent' ? value * 100 : value) /
    truncationValues[magnitudeTruncation.toString()];

  return `${prefix}${Highcharts.numberFormat(adjustedValue, decimalPlaces, '.')
    .toString()
    .replaceAll(' ', '')
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${suffix}`;
};

// Helper to get locale-specific decimal separator and thousands separator
export const getSeparators = (): { decimal: string; thousands: string } => {
  const europeanLocales = ['be', 'fr', 'es', 'nl', 'pt', 'ru'];
  let decimalSeparator = '.';
  let thousandsSeparator = ',';

  try {
    const formatter = new Intl.NumberFormat(navigator.language || 'en-US');
    const parts = formatter.formatToParts(1.1);
    const decimalPart = parts.find((p) => p.type === 'decimal');
    decimalSeparator = decimalPart ? decimalPart.value : '.';

    // Get thousands separator from formatted large number
    const largeFormatted = formatter.format(1000);
    const thousandsPart = largeFormatted.charAt(1);
    thousandsSeparator =
      thousandsPart && thousandsPart !== '0'
        ? thousandsPart
        : decimalSeparator === '.'
          ? ','
          : '.';
  } catch (e) {
    console.warn('Intl.NumberFormat failed, using default separators');
  }

  // Only override if PRIMARY language (navigator.language) is European
  // Don't override if it's just one of the secondary languages
  const primaryLang = navigator.language?.toLowerCase() || '';
  if (europeanLocales.some((locale) => primaryLang.includes(locale))) {
    decimalSeparator = ',';
    thousandsSeparator = '.';
  }

  return { decimal: decimalSeparator, thousands: thousandsSeparator };
};

// Helper to get the appropriate regex based on decimal separator
const getNumberRegex = (decimal: string): RegExp => {
  if (decimal === '.') {
    // US: thousands=comma ONLY (no spaces allowed)
    // Valid: 1,234.56 or 1234.56 or 0.45
    // Invalid: 1.234,56 (wrong decimal) or 1 234.56 (space as thousands)
    return /^-?(\d{1,3}(,\d{3})*(\.\d+)?|\d+(\.\d+)?)$/;
  } else {
    // French/European: thousands=period or space, decimal=comma
    // Valid: 1.234,56 or 1 234,56 or 1234,56 or 0,45
    // Invalid: 1,234.56 (wrong decimal)
    return /^-?(\d{1,3}([.\s]\d{3})*(,\d+)?|\d+(,\d+)?)$/;
  }
};

// Validate decimal input with locale support (allows thousands separator)
export const isValidDecimal = (value: string): boolean => {
  const { decimal } = getSeparators();
  const cleanedValue = String(value).trim();
  const regex = getNumberRegex(decimal);
  const isValid = regex.test(cleanedValue);
  return isValid;
};

// Validate percentage input with locale support (allows thousands separator)
export const isValidPercentage = (value: string): boolean => {
  const { decimal } = getSeparators();
  const cleanedValue = String(value).trim().replace('%', '').trim();
  return getNumberRegex(decimal).test(cleanedValue);
};
